/**
 * DesktopShell — slim composed replacement for the legacy DesktopLayout.
 *
 * This is the modular drop-in built entirely from the Tier 1–4 pieces.
 * The legacy `DesktopLayout.tsx` is still untouched in this directory;
 * to switch over, change the import in `App.tsx` from:
 *
 *   import DesktopLayout from './pages/DesktopLayout'
 *
 * to:
 *
 *   import DesktopShell from './pages/DesktopShell'
 *
 * …and update the route element. Nothing else has to change.
 *
 * Composition map:
 *   - TopBar             ← components/layout/TopBar
 *   - Sidebar            ← components/layout/Sidebar
 *   - HomeSection        ← components/home/HomeSection
 *   - PersonnelSection   ← components/personnel/PersonnelSection
 *   - RosterPanel        ← components/personnel/RosterPanel  (legacy, untouched)
 *   - BadgePanel         ← components/equipment/BadgePanel
 *   - KeySection         ← components/equipment/KeySection
 *   - SupRadioPanel      ← components/equipment/SupRadioPanel
 *   - OffRadioPanel      ← components/equipment/OffRadioPanel
 *   - OtherRadioPanel    ← components/equipment/OtherRadioPanel
 *   - RecorderPanel      ← components/equipment/RecorderPanel
 *   - FlashlightSection  ← components/equipment/FlashlightSection
 *   - AccessBadgePanel   ← components/equipment/AccessBadgePanel
 *   - SatPhonePanel      ← components/equipment/SatPhonePanel
 *   - NotesPanel         ← components/equipment/NotesPanel
 *   - ToolsPanel         ← components/layout/ToolsPanel
 */
import { useState } from 'react'
import { useAuthStore, getAccessLevel } from '../stores/auth'
import { useDesktopStore } from '../stores/desktop'
import { useTenantStore } from '../stores/tenant'

import TopBar  from '../components/layout/TopBar'
import Sidebar from '../components/layout/Sidebar'
import ToolsPanel from '../components/layout/ToolsPanel'
import PostingModal from '../components/layout/PostingModal'

import CommandDashboard from '../components/command/CommandDashboard'
import CadBoard         from '../components/cad/CadBoard'
import MallActivityMap  from '../components/cad/MallActivityMap'
import ZoneEditor       from '../components/cad/ZoneEditor'
import HomeSection      from '../components/home/HomeSection'
import PersonnelSection from '../components/personnel/PersonnelSection'
import RosterPanel      from '../components/personnel/RosterPanel'

import BadgePanel        from '../components/equipment/BadgePanel'
import KeySection        from '../components/equipment/KeySection'
import SupRadioPanel     from '../components/equipment/SupRadioPanel'
import OffRadioPanel     from '../components/equipment/OffRadioPanel'
import OtherRadioPanel   from '../components/equipment/OtherRadioPanel'
import RecorderPanel     from '../components/equipment/RecorderPanel'
import FlashlightSection from '../components/equipment/FlashlightSection'
import AccessBadgePanel  from '../components/equipment/AccessBadgePanel'
import SatPhonePanel     from '../components/equipment/SatPhonePanel'
import NotesPanel        from '../components/equipment/NotesPanel'

const HEADER_HEIGHT = 48
const TOOLS_PANEL_W = 88

const PRIVILEGED_LEVELS = ['developer', 'exec', 'supervisor'] as const

function LockedFeatureCard({ name, requiredTier, description }: { name: string; requiredTier: string; description: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 320, background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: 40, textAlign: 'center', gap: 12,
    }}>
      <div style={{ fontSize: 48 }}>🔒</div>
      <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 18 }}>{name}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 14, maxWidth: 360 }}>{description}</div>
      <div style={{
        background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)',
        borderRadius: 8, padding: '8px 16px', color: '#c9a84c', fontSize: 13, fontWeight: 600,
      }}>
        Requires {requiredTier} plan
      </div>
      <button
        onClick={() => window.location.href = '/#/signup'}
        style={{
          marginTop: 8, padding: '10px 24px',
          background: 'linear-gradient(135deg,#c9a84c,#d97706)',
          color: '#0a1628', border: 'none', borderRadius: 8,
          fontWeight: 700, fontSize: 14, cursor: 'pointer',
        }}
      >
        Upgrade Plan →
      </button>
    </div>
  )
}

export default function DesktopShell() {
  const session = useAuthStore(s => s.session)
  const { activeSection, setActiveSection } = useDesktopStore()
  const features = useTenantStore(s => s.features)
  const [toolsOpen, setToolsOpen] = useState(false)
  const [postingOpen, setPostingOpen] = useState(false)
  const [rosterOpen, setRosterOpen] = useState(false)

  // Use the shared getAccessLevel helper (substring-matching, OIC-aware) instead
  // of an exact-rank whitelist. This keeps privilege checks consistent with the
  // Worker's sessionLevel() and with every other frontend check.
  const isPriv = session ? PRIVILEGED_LEVELS.includes(getAccessLevel(session) as any) : false

  function renderSection() {
    switch (activeSection) {
      case 'top':
        return <HomeSection />
      case 'sec-personnel':
        return <PersonnelSection />
      case 'sec-officer-badges':
        return <BadgePanel />
      case 'sec-security-keys':
        return <KeySection />
      case 'sec-sup-radios':
        return (
          <>
            <SupRadioPanel />
            <OffRadioPanel />
            <OtherRadioPanel />
          </>
        )
      case 'sec-recorders':
        return <RecorderPanel />
      case 'sec-flashlights':
      case 'sec-others':
        return (
          <>
            <FlashlightSection />
            <div style={{ height: 24 }} />
            <AccessBadgePanel />
            <SatPhonePanel />
          </>
        )
      case 'sec-notes':
        return <NotesPanel />
      // ── Gated features ──
      case 'sec-terrain':
        return features.terrain
          ? <div style={{ color: 'var(--text-primary)', padding: 24 }}>Terrain Map — coming soon</div>
          : <LockedFeatureCard name="Terrain Map" requiredTier="Terrain+NFC ($149/mo)" description="Track officer locations in real time on an interactive terrain map with geofenced checkpoints." />
      case 'sec-nfc':
        return features.nfc
          ? <div style={{ color: 'var(--text-primary)', padding: 24 }}>NFC Checkpoints — coming soon</div>
          : <LockedFeatureCard name="NFC Checkpoints" requiredTier="Terrain+NFC ($149/mo)" description="Officers tap NFC tags at checkpoints to log verified patrol rounds automatically." />
      case 'sec-cad':
        return features.cad
          ? <CadBoard />
          : <LockedFeatureCard name="Gatehouse" requiredTier="Command ($399/mo)" description="Log and dispatch daily security activity with a live call board and unit tracking." />
      case 'sec-activity-map':
        return features.cad
          ? <MallActivityMap />
          : <LockedFeatureCard name="Activity Map" requiredTier="Command ($399/mo)" description="See today's calls plotted on the mall floor plan by wing, with a Lower/Upper level toggle." />
      case 'sec-zone-editor':
        return features.cad
          ? <ZoneEditor />
          : <LockedFeatureCard name="Zone Editor" requiredTier="Command ($399/mo)" description="Trace tenant spaces onto the floor plan so the activity map can highlight individual suites." />
      case 'sec-incident':
        return features.incident
          ? <div style={{ color: 'var(--text-primary)', padding: 24 }}>Incident Reports — coming soon</div>
          : <LockedFeatureCard name="Incident Reports" requiredTier="Command ($399/mo)" description="File, track, and export structured incident reports with photo attachments." />
      case 'sec-command':
        return features.command
          ? <CommandDashboard />
          : <LockedFeatureCard name="Command Dashboard" requiredTier="Command ($399/mo)" description="Multi-site overview, real-time officer status, and executive reporting in one dashboard." />
      default:
        return <HomeSection />
    }
  }

  return (
    <div
      style={{
        // 100dvh (dynamic viewport height) tracks the REAL visible area on
        // tablets/phones where the browser address bar grows and shrinks.
        // Plain 100vh measures the largest viewport (bar retracted), making
        // the shell taller than the screen — the body scrolled and the
        // bottom of the dashboard bled past the visible edge.
        height: '100dvh',
        overflow: 'hidden',
        overscrollBehavior: 'none',
        background: 'var(--bg-deep)',
        color: 'var(--text-primary)',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      <TopBar height={HEADER_HEIGHT} onPostingClick={() => setPostingOpen(true)} onGatehouseClick={() => setActiveSection('sec-cad')} />

      <div style={{ display: 'flex', marginTop: HEADER_HEIGHT }}>
        <Sidebar
          headerHeight={HEADER_HEIGHT}
          activeSection={activeSection}
          toolsOpen={toolsOpen}
          onSelect={id => {
            if (id === 'sec-roster') {
              setRosterOpen(true)
            } else {
              setActiveSection(id)
            }
          }}
          onToggleTools={() => setToolsOpen(o => !o)}
        />

        <div
          style={{
            marginLeft: 52,
            flex: 1,
            overflowY: 'auto',
            height: `calc(100dvh - ${HEADER_HEIGHT}px)`,
            overscrollBehavior: 'contain',
            padding: 16,
            paddingRight: toolsOpen ? TOOLS_PANEL_W + 16 : 16,
            transition: 'padding-right 0.25s ease',
            boxSizing: 'border-box',
          }}
        >
          {renderSection()}
        </div>
      </div>

      <ToolsPanel
        open={toolsOpen}
        isPriv={isPriv}
        panelWidth={TOOLS_PANEL_W}
        headerHeight={HEADER_HEIGHT}
        onNavigate={id => {
          if (id === 'sec-roster') {
            setRosterOpen(true)
          } else {
            setActiveSection(id)
          }
        }}
        onClose={() => setToolsOpen(false)}
        onPostingClick={() => setPostingOpen(true)}
      />

      {postingOpen && <PostingModal onClose={() => setPostingOpen(false)} />}
      {rosterOpen && <RosterPanel onClose={() => setRosterOpen(false)} />}
    </div>
  )
}
